/**
 * AppRoutes
 *
 * Houses every lazy-loaded page and the full `<Routes>` tree for the
 * authenticated admin portal. Extracted from `App.tsx` to keep the shell
 * readable; per-domain splitting (loans.routes.tsx, compliance.routes.tsx, …)
 * is a future follow-up.
 *
 * NOTE: Ordering of `<Route>` elements is preserved verbatim from the
 * original App.tsx — React Router v6 first-match semantics matter (e.g. the
 * legacy `/transactions` placeholder intentionally shadows the real
 * `<TransactionList />` route further down until that is cleaned up).
 */

import React, { lazy } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext.js';
import { ProtectedRoute } from '../auth/components/ProtectedRoute';
import { PERMISSIONS } from '../constants/permissions';

// ---------------------------------------------------------------------------
// Lazy-loaded route pages
// ---------------------------------------------------------------------------

// Eagerly-imported in the original App.tsx; kept lazy here so AppRoutes does
// not pull synchronous component code at import time.
const Unauthorized = lazy(() => import('../components/Unauthorized'));
const Dashboard = lazy(() => import('../components/Dashboard/Dashboard'));
const Settings = lazy(() => import('../components/Settings/Settings'));
const NewInstitution = lazy(() => import('../components/SuperAdmin/NewInstitution.js'));
const Institutions = lazy(() => import('../components/SuperAdmin/Institutions'));
const InstitutionDetails = lazy(() => import('../components/SuperAdmin/InstitutionDetails'));
const Users = lazy(() => import('../components/Users/Users'));
const UserDetail = lazy(() => import('../components/Users/UserDetail'));
const Profile = lazy(() => import('../components/Profile/Profile'));
const Customers = lazy(() => import('../components/Customers').then(m => ({ default: m.Customers })));
const RetailUserDetail = lazy(() => import('../components/Customers/RetailUserDetail'));
const VehicleLoans = lazy(() => import('../components/VehicleLoans/VehicleLoans').then(m => ({ default: m.VehicleLoans })));
const DepositProductsPage = lazy(() => import('../features/deposits/pages/DepositProducts'));
const DepositAccountsPage = lazy(() => import('../features/deposits/pages/Accounts'));
const InterestRatesPage = lazy(() => import('../features/deposits/pages/InterestRates'));
const MaturitiesPage = lazy(() => import('../features/deposits/pages/Maturities'));
const DepositApplicationsPage = lazy(() => import('../features/deposits/pages/Applications'));
const DepositNoticesPage = lazy(() => import('../features/deposits/pages/Notices'));
const DepositReportsPage = lazy(() => import('../features/deposits/pages/Reports'));
const DepositSettingsPage = lazy(() => import('../features/deposits/pages/Settings'));
const DepositTransfersPage = lazy(() => import('../features/deposits/pages/Transfers'));
const LoansDashboard = lazy(() => import('../features/loans/pages/LoansDashboard'));
const ApplicationsPipeline = lazy(() => import('../features/loans/pages/ApplicationsPipeline'));
const ApplicationWizard = lazy(() => import('../features/loans/pages/ApplicationWizard'));
const ApplicationWorkspace = lazy(() => import('../features/loans/pages/ApplicationWorkspace'));
const LoanPipeline = lazy(() => import('../features/loans/pages/LoanPipeline'));
const LoanApplicationDetail = lazy(() => import('../features/loans/pages/LoanApplicationDetail'));
const CreditDecisioning = lazy(() => import('../features/loans/pages/CreditDecisioning'));
const DisbursementPage = lazy(() => import('../features/loans/pages/Disbursement'));
const PortfolioRisk = lazy(() => import('../features/loans/pages/PortfolioRisk'));
const AccountsPage = lazy(() => import('../features/loans/pages/AccountsPage'));
const AccountWorkspace = lazy(() => import('../features/loans/pages/AccountWorkspace').then(m => ({ default: m.AccountWorkspace })));
const CollectionsDashboard = lazy(() => import('../features/loans/pages/CollectionsDashboard').then(m => ({ default: m.CollectionsDashboard })));
const CollectionsWorkspace = lazy(() => import('../features/loans/pages/CollectionsWorkspace'));

// Admin backend-wired loan pages (Phase 9C.4)
const AdminLoanPipeline = lazy(() => import('../pages/loans/LoanPipeline'));
const AdminLoanApplicationDetail = lazy(() => import('../pages/loans/LoanApplicationDetail'));
const AdminLoanProducts = lazy(() => import('../pages/loans/LoanProducts'));
const AdminLoanPortfolio = lazy(() => import('../pages/loans/LoanPortfolio'));
const AdminLoanPortfolioDetail = lazy(() => import('../pages/loans/LoanPortfolioDetail'));
const AdminLoanCollections = lazy(() => import('../pages/loans/LoanCollections'));

// Admin backend-wired mortgage pages (Phase 9C.5)
const AdminMortgagePipeline = lazy(() => import('../pages/mortgages/MortgagePipeline'));
const AdminMortgageProducts = lazy(() => import('../pages/mortgages/MortgageProducts'));
const AdminMortgagePortfolio = lazy(() => import('../pages/mortgages/MortgagePortfolio'));
const AdminMortgageDetail = lazy(() => import('../pages/mortgages/MortgageDetail'));

// Admin backend-wired card pages (Phase 9C.6)
const AdminCardList = lazy(() => import('../pages/cards/CardList'));
const AdminCardDetail = lazy(() => import('../pages/cards/CardDetail'));
const AdminCardDisputes = lazy(() => import('../pages/cards/CardDisputes'));
const ProductsPage = lazy(() => import('../features/loans/pages/ProductsPage'));
const LoansReportsPage = lazy(() => import('../features/loans/pages/ReportsPage'));
const PayByBank = lazy(() => import('../components/Payments/PayByBank'));
const OpenBankingRoutes = lazy(() => import('../features/openbanking/routes').then(m => ({ default: m.OpenBankingRoutes })));
const ApiManagement = lazy(() => import('../features/api-management/pages/ApiManagement'));
const GatewayRouteList = lazy(() => import('../features/gateway-routes/pages/RouteList'));
const GatewayRouteDetail = lazy(() => import('../features/gateway-routes/pages/RouteDetail'));
const GatewayCreateRoute = lazy(() => import('../features/gateway-routes/pages/CreateRoute'));

// Institution Builder
const InstitutionBuilder = lazy(() => import('../features/institution-builder/pages/InstitutionBuilder'));

// Account Management
const AccountList = lazy(() => import('../pages/accounts/AccountList'));
const AccountDetail = lazy(() => import('../pages/accounts/AccountDetail'));

// Payments Hub (9C.7)
const AdminPaymentList = lazy(() => import('../pages/payments/PaymentList'));
const AdminPaymentDetail = lazy(() => import('../pages/payments/PaymentDetail'));
const AdminInitiatePayment = lazy(() => import('../pages/payments/InitiatePayment'));
const AdminPaymentSchemes = lazy(() => import('../pages/payments/PaymentSchemes'));
const AdminStandingOrders = lazy(() => import('../pages/payments/StandingOrders'));
const AdminDirectDebits = lazy(() => import('../pages/payments/DirectDebits'));

// GL Enrichment (9C.8)
const AdminChartOfAccounts = lazy(() => import('../pages/gl/ChartOfAccounts'));
const AdminJournalEntries = lazy(() => import('../pages/gl/JournalEntries'));
const AdminTrialBalance = lazy(() => import('../pages/gl/TrialBalance'));
const AdminFinancialStatements = lazy(() => import('../pages/gl/FinancialStatements'));
const AdminCostCentres = lazy(() => import('../pages/gl/CostCentres'));
const AdminReconciliation = lazy(() => import('../pages/gl/Reconciliation'));
const AdminPeriodManagement = lazy(() => import('../pages/gl/PeriodManagement'));

// Smart Panel
const SmartPanelMessages = lazy(() => import('../features/smart-panel/pages/SmartPanelMessages'));
const MessageEditor = lazy(() => import('../features/smart-panel/pages/MessageEditor'));

// Rewards
const RewardsConfig = lazy(() => import('../features/rewards/pages/RewardsConfig'));
const RewardsBadges = lazy(() => import('../features/rewards/pages/RewardsBadges'));
const RewardsTiers = lazy(() => import('../features/rewards/pages/RewardsTiers'));
const RewardsOffers = lazy(() => import('../features/rewards/pages/RewardsOffers'));
const RewardsAnalytics = lazy(() => import('../features/rewards/pages/RewardsAnalytics'));

// Insights
const InsightsManager = lazy(() => import('../features/insights/pages/InsightsManager'));

// Journey Templates
const TemplateList = lazy(() => import('../features/journeys-admin/pages/TemplateList'));
const TemplateEditor = lazy(() => import('../features/journeys-admin/pages/TemplateEditor'));

// Profile Traits
const TraitsManager = lazy(() => import('../features/profile-traits/pages/TraitsManager'));

// Branch Banking
const BranchDashboard = lazy(() => import('../features/branch-banking/pages/BranchDashboard'));
const CustomerLookup = lazy(() => import('../features/branch-banking/pages/CustomerLookup'));
const AccountOpening = lazy(() => import('../features/branch-banking/pages/AccountOpening'));
const BranchTransactions = lazy(() => import('../features/branch-banking/pages/Transactions'));
const CashManagement = lazy(() => import('../features/branch-banking/pages/CashManagement'));
const TellerQueue = lazy(() => import('../features/branch-banking/pages/TellerQueue'));

// Global Search
const SearchResults = lazy(() => import('../features/global-search/pages/SearchResults'));

// Audit Trail
const AuditDashboard = lazy(() => import('../features/audit-trail/pages/AuditDashboard'));
const AuditLog = lazy(() => import('../features/audit-trail/pages/AuditLog'));
const AuditUserActivity = lazy(() => import('../features/audit-trail/pages/UserActivity'));

// General Ledger
const GLDashboard = lazy(() => import('../features/general-ledger/pages/GLDashboard'));
const ChartOfAccounts = lazy(() => import('../features/general-ledger/pages/ChartOfAccounts'));
const JournalEntries = lazy(() => import('../features/general-ledger/pages/JournalEntries'));
const JournalDetail = lazy(() => import('../features/general-ledger/pages/JournalDetail'));
const TrialBalance = lazy(() => import('../features/general-ledger/pages/TrialBalance'));

// Mortgages
const MortgagePipeline = lazy(() => import('../features/mortgages/pages/MortgagePipeline'));
const MortgageApplicationDetail = lazy(() => import('../features/mortgages/pages/MortgageApplicationDetail'));
const MortgageProducts = lazy(() => import('../features/mortgages/pages/MortgageProducts'));
const AffordabilityView = lazy(() => import('../features/mortgages/pages/AffordabilityView'));
const MortgageReports = lazy(() => import('../features/mortgages/pages/MortgageReports'));

// Payment Operations
const PaymentsDashboard = lazy(() => import('../features/payment-ops/pages/PaymentsDashboard'));
const PaymentSearch = lazy(() => import('../features/payment-ops/pages/PaymentSearch'));
const PaymentOpsReconciliation = lazy(() => import('../features/payment-ops/pages/Reconciliation'));
const PaymentOpsStandingOrders = lazy(() => import('../features/payment-ops/pages/StandingOrders'));
const PaymentOpsFailedPayments = lazy(() => import('../features/payment-ops/pages/FailedPayments'));
const PaymentOpsSchemeRouting = lazy(() => import('../features/payment-ops/pages/SchemeRouting'));

// Compliance
const ComplianceDashboard = lazy(() => import('../features/compliance/pages/ComplianceDashboard'));
const AlertManagement = lazy(() => import('../features/compliance/pages/AlertManagement'));
const SarFiling = lazy(() => import('../features/compliance/pages/SarFiling'));
const RegulatoryReturns = lazy(() => import('../features/compliance/pages/RegulatoryReturns'));
const SanctionsScreening = lazy(() => import('../features/compliance/pages/SanctionsScreening'));

// Compliance Centre (Phase 9C.9)
const AdminSARManagement = lazy(() => import('../pages/compliance/SARManagement'));
const AdminSanctionsScreening = lazy(() => import('../pages/compliance/SanctionsScreening'));
const AdminTransactionAlerts = lazy(() => import('../pages/compliance/TransactionAlerts'));
const AdminRegulatoryReports = lazy(() => import('../pages/compliance/RegulatoryReports'));
const AdminCapitalAdequacy = lazy(() => import('../pages/compliance/CapitalAdequacy'));
const AdminIFRS9Dashboard = lazy(() => import('../pages/compliance/IFRS9Dashboard'));
const AdminRiskWeightedAssets = lazy(() => import('../pages/compliance/RiskWeightedAssets'));

// Branch Banking Integration (Phase 9C.11)
const AdminTellerDashboard = lazy(() => import('../pages/branch/TellerDashboard'));
const AdminVaultManagement = lazy(() => import('../pages/branch/VaultManagement'));
const AdminEODReconciliation = lazy(() => import('../pages/branch/EODReconciliation'));
const AdminBranchOverview = lazy(() => import('../pages/branch/BranchOverview'));

// Notifications Centre (Phase 9C.14)
const AdminNotificationTemplates = lazy(() => import('../pages/notifications/NotificationTemplates'));
const AdminDeliveryHistory = lazy(() => import('../pages/notifications/DeliveryHistory'));
const AdminChannelConfig = lazy(() => import('../pages/notifications/ChannelConfig'));
const AdminCustomerPreferences = lazy(() => import('../pages/notifications/CustomerPreferences'));

// KYB Centre (Phase 9C.10)
const KYBEntityList = lazy(() => import('../pages/kyb/EntityList'));
const KYBEntityDetail = lazy(() => import('../pages/kyb/EntityDetail'));
const KYBReviewQueue = lazy(() => import('../pages/kyb/ReviewQueue'));
const KYBScreeningDashboard = lazy(() => import('../pages/kyb/ScreeningDashboard'));
const KYBOwnershipStructure = lazy(() => import('../features/kyb/pages/OwnershipStructure'));

// Account Opening
const OpenAccount = lazy(() => import('../pages/accounts/OpenAccount'));

// Audit Report Builder
const AuditReportBuilder = lazy(() => import('../features/audit-trail/pages/ReportBuilder'));

// Product Factory (Phase 9C.12)
const AdminProductCatalog = lazy(() => import('../pages/admin/products/ProductCatalog'));
const AdminProductDetail = lazy(() => import('../pages/admin/products/ProductDetail'));
const AdminProductBuilder = lazy(() => import('../pages/admin/products/ProductBuilder'));
const AdminProductComparison = lazy(() => import('../pages/admin/products/ProductComparison'));

// Limits & Controls (Phase 9C.13)
const AdminLimitDefinitions = lazy(() => import('../pages/admin/limits/LimitDefinitions'));
const AdminInstitutionLimits = lazy(() => import('../pages/admin/limits/InstitutionLimits'));
const AdminCustomerLimitOverrides = lazy(() => import('../pages/admin/limits/CustomerLimitOverrides'));
const AdminLimitHistory = lazy(() => import('../pages/admin/limits/LimitHistory'));

// Provider Registry Admin (Phase 9CX.6)
const AdminProviderCatalog = lazy(() => import('../pages/admin/providers/ProviderCatalog'));
const AdminProviderContracts = lazy(() => import('../pages/admin/providers/ProviderContracts'));
const AdminProviderHealth = lazy(() => import('../pages/admin/providers/ProviderHealth'));
const AdminFeatureMatrix = lazy(() => import('../pages/admin/providers/FeatureMatrix'));
const AdminProviderSwap = lazy(() => import('../pages/admin/providers/ProviderSwap'));
const AdminApiMetering = lazy(() => import('../pages/admin/providers/ApiMetering'));

// Fee Engine Admin (Phase 9CX.7)
const AdminFeeDefinitions = lazy(() => import('../pages/admin/fees/FeeDefinitions'));
const AdminFeeSchedules = lazy(() => import('../pages/admin/fees/FeeSchedules'));
const AdminFeeSimulator = lazy(() => import('../pages/admin/fees/FeeSimulator'));

// Open Banking Admin (Phase 9CX.9)
const AdminOpenBankingConfig = lazy(() => import('../pages/admin/openbanking/OpenBankingConfig'));
const AdminConnectionMonitor = lazy(() => import('../pages/admin/openbanking/ConnectionMonitor'));
const AdminOpenBankingAlerts = lazy(() => import('../pages/admin/openbanking/OpenBankingAlerts'));
const AdminOpenBankingStats = lazy(() => import('../pages/admin/openbanking/OpenBankingStats'));
const AdminConsentOversight = lazy(() => import('../pages/admin/openbanking/ConsentOversight'));

// Rules Config (Phase 9CX.1)
const AdminRulesConfig = lazy(() => import('../pages/admin/RulesConfig'));

// Neural Engine Admin (Phase 9CX.2)
const AdminPersonalityManager = lazy(() => import('../pages/admin/neural/PersonalityManager'));
const AdminNeuralRules = lazy(() => import('../pages/admin/neural/NeuralRules'));
const AdminFeedbackDashboard = lazy(() => import('../pages/admin/neural/FeedbackDashboard'));
const AdminConversationBrowser = lazy(() => import('../pages/admin/neural/ConversationBrowser'));
const AdminMoniAIFeedbackIssues = lazy(() => import('../pages/admin/moni-ai/FeedbackIssues'));

// ConfigStore Admin (Phase 9CX.12)
const AdminConfigBrowser = lazy(() => import('../pages/admin/config/ConfigBrowser'));
const AdminConfigHistory = lazy(() => import('../pages/admin/config/ConfigHistory'));
const AdminConfigExportImport = lazy(() => import('../pages/admin/config/ConfigExportImport'));

// AI Reports & Data Explorer (Phase 9D.3 / 9D.4)
const AIReports = lazy(() => import('../pages/ai/AIReports'));
const AIReportDetail = lazy(() => import('../pages/ai/AIReportDetail'));
const AIDataExplorer = lazy(() => import('../pages/ai/AIDataExplorer'));

// Platform Management – Phase 9E.1 System Health
const PlatformSystemHealth = lazy(() => import('../pages/platform/SystemHealth'));
const PlatformServiceDetail = lazy(() => import('../pages/platform/ServiceDetail'));
const PlatformInfrastructureHealth = lazy(() => import('../pages/platform/InfrastructureHealth'));

// Platform Management – Phase 9E.2 Metering & Billing
const PlatformMeteringDashboard = lazy(() => import('../pages/platform/MeteringDashboard'));
const PlatformUsageDetail = lazy(() => import('../pages/platform/UsageDetail'));
const PlatformBillingPlans = lazy(() => import('../pages/platform/BillingPlans'));
const PlatformInvoiceManagement = lazy(() => import('../pages/platform/InvoiceManagement'));
const PlatformMigrationConsole = lazy(() => import('../pages/platform/MigrationConsole'));

// Transactions (Phase 10)
const TransactionList = lazy(() => import('../pages/transactions/TransactionList'));
const TransactionDetail = lazy(() => import('../pages/transactions/TransactionDetail'));

// Insurance Admin (Phase 9C.18)
const AdminInsuranceProducts = lazy(() => import('../pages/admin/insurance/InsuranceProducts'));
const AdminInsuranceQuotes = lazy(() => import('../pages/admin/insurance/InsuranceQuotes'));
const AdminInsuranceUnderwriting = lazy(() => import('../pages/admin/insurance/InsuranceUnderwriting'));
const AdminInsuranceClaims = lazy(() => import('../pages/admin/insurance/InsuranceClaims'));

// KYC Management
const KycDashboard = lazy(() => import('../features/kyc/pages/KycDashboard'));
const KycAnalytics = lazy(() => import('../features/kyc/pages/KycAnalytics'));
const KycReviews = lazy(() => import('../features/kyc/pages/KycReviews'));


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PageLoader = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        color: 'var(--color-text-secondary)'
    }}>
        <div>Loading...</div>
    </div>
);
const AdminDashboard = () => {
    const { theme } = useTheme();
    return <div style={{ color: theme === 'dark' ? '#fff' : '#000' }}>Admin Dashboard</div>;
};
const JourneyBuilderRedirect = () => {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/journey-templates/${id}`} replace />;
};

// ---------------------------------------------------------------------------
// <AppRoutes />
// ---------------------------------------------------------------------------

function AppRoutes() {
    const { theme } = useTheme();

    return (
        <Routes>
                                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                                <Route path="/dashboard" element={<Dashboard />} />

                                {/* Customer Routes - requires customers:view */}
                                <Route path="/customers" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CUSTOMERS_VIEW]}>
                                        <Customers />
                                    </ProtectedRoute>
                                } />
                                <Route path="/customers/:customerId" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CUSTOMERS_VIEW]}>
                                        <RetailUserDetail />
                                    </ProtectedRoute>
                                } />

                                {/* Vehicle Loans - uses loan-related permissions */}
                                <Route path="/vehicle-loans" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_VIEW]}>
                                        <VehicleLoans />
                                    </ProtectedRoute>
                                } />

                                {/* Transactions - requires transactions:view */}
                                <Route path="/transactions" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.TRANSACTIONS_VIEW]}>
                                        <div style={{ color: theme === 'dark' ? '#fff' : '#000' }}>Transactions Page</div>
                                    </ProtectedRoute>
                                } />

                                {/* Payment Routes - requires transactions:create */}
                                <Route path="/payments/bank-transfer" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.TRANSACTIONS_CREATE]}>
                                        <PayByBank />
                                    </ProtectedRoute>
                                } />

                                {/* Open Banking Routes - requires accounts:view */}
                                <Route path="/openbanking/*" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.ACCOUNTS_VIEW]}>
                                        <OpenBankingRoutes />
                                    </ProtectedRoute>
                                } />

                                {/* Account Management Routes - requires accounts:view */}
                                <Route path="/accounts" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.ACCOUNTS_VIEW]}>
                                        <AccountList />
                                    </ProtectedRoute>
                                } />
                                <Route path="/accounts/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.ACCOUNTS_VIEW]}>
                                        <AccountDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/accounts/new" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.ACCOUNTS_CREATE]}>
                                        <OpenAccount />
                                    </ProtectedRoute>
                                } />

                                {/* Institutions Routes - requires platform:institutions:manage */}
                                <Route path="/institutions/new" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_INSTITUTIONS_CREATE]}>
                                        <NewInstitution />
                                    </ProtectedRoute>
                                } />
                                <Route path="/institutions" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE]}>
                                        <Institutions />
                                    </ProtectedRoute>
                                } />
                                <Route path="/institutions/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE]}>
                                        <InstitutionDetails />
                                    </ProtectedRoute>
                                } />
                                <Route path="/institutions/:id/app-config" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE]}>
                                        <InstitutionBuilder />
                                    </ProtectedRoute>
                                } />

                                {/* Users Routes - requires users:view */}
                                <Route path="/users" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.USERS_VIEW]}>
                                        <Users />
                                    </ProtectedRoute>
                                } />
                                <Route path="/users/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.USERS_VIEW]}>
                                        <UserDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/users/:id/edit" element={
                                    <ProtectedRoute
                                        requiredPermissions={[
                                            PERMISSIONS.USERS_EDIT,
                                            PERMISSIONS.USERS_ASSIGN_ROLES,
                                        ]}
                                        requireAny
                                    >
                                        <UserDetail />
                                    </ProtectedRoute>
                                } />

                                {/* Profile Route - always accessible to authenticated users */}
                                <Route path="/profile" element={<Profile />} />

                                {/* Deposit Products Route - requires savings:view */}
                                <Route path="/deposit-products" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_VIEW]}>
                                        <DepositProductsPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/deposits/products" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_VIEW]}>
                                        <DepositProductsPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/deposits/accounts" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_VIEW]}>
                                        <DepositAccountsPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/deposits/rates" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_MANAGE]}>
                                        <InterestRatesPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/deposits/maturities" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_VIEW]}>
                                        <MaturitiesPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/deposits/applications" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_VIEW]}>
                                        <DepositApplicationsPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/deposits/notices" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_VIEW]}>
                                        <DepositNoticesPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/deposits/reports" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_VIEW]}>
                                        <DepositReportsPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/deposits/settings" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_MANAGE]}>
                                        <DepositSettingsPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/deposits/transfers" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SAVINGS_VIEW]}>
                                        <DepositTransfersPage />
                                    </ProtectedRoute>
                                } />

                                {/* API Management Route- requires settings:security */}
                                <Route path="/api-management" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS_SECURITY]}>
                                        <ApiManagement />
                                    </ProtectedRoute>
                                } />

                                {/* Gateway Routes - requires platform:routes:manage */}
                                <Route path="/gateway/routes" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_ROUTES_MANAGE]}>
                                        <GatewayRouteList />
                                    </ProtectedRoute>
                                } />
                                <Route path="/gateway/routes/new" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_ROUTES_MANAGE]}>
                                        <GatewayCreateRoute />
                                    </ProtectedRoute>
                                } />
                                <Route path="/gateway/routes/:routeId" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_ROUTES_MANAGE]}>
                                        <GatewayRouteDetail />
                                    </ProtectedRoute>
                                } />

                                {/* Loans Routes */}
                                <Route path="/loans" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_VIEW]}>
                                        <LoansDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/pipeline" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_VIEW]}>
                                        <AdminLoanPipeline />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/applications" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_VIEW]}>
                                        <ApplicationsPipeline />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/applications/new" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_CREATE]}>
                                        <ApplicationWizard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/applications/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_VIEW]}>
                                        <AdminLoanApplicationDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/applications/:id/detail" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_VIEW]}>
                                        <LoanApplicationDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/credit-decisioning" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_APPROVE]}>
                                        <CreditDecisioning />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/disbursement" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_DISBURSE]}>
                                        <DisbursementPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/portfolio" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_VIEW]}>
                                        <AdminLoanPortfolio />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/portfolio/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_VIEW]}>
                                        <AdminLoanPortfolioDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/accounts" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.ACCOUNTS_VIEW]}>
                                        <AccountsPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/accounts/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.ACCOUNTS_VIEW]}>
                                        <AccountWorkspace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/collections" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_MANAGE]}>
                                        <AdminLoanCollections />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/collections/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_MANAGE]}>
                                        <CollectionsWorkspace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/products" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LOANS_VIEW]}>
                                        <AdminLoanProducts />
                                    </ProtectedRoute>
                                } />
                                <Route path="/loans/reports" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS_VIEW]}>
                                        <LoansReportsPage />
                                    </ProtectedRoute>
                                } />

                                {/* Mortgage Routes */}
                                <Route path="/mortgages/pipeline" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.MORTGAGE_VIEW]}>
                                        <AdminMortgagePipeline />
                                    </ProtectedRoute>
                                } />
                                <Route path="/mortgages/applications/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.MORTGAGE_VIEW]}>
                                        <MortgageApplicationDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/mortgages/products" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.MORTGAGE_VIEW]}>
                                        <AdminMortgageProducts />
                                    </ProtectedRoute>
                                } />
                                <Route path="/mortgages/portfolio" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.MORTGAGE_VIEW]}>
                                        <AdminMortgagePortfolio />
                                    </ProtectedRoute>
                                } />
                                <Route path="/mortgages/portfolio/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.MORTGAGE_VIEW]}>
                                        <AdminMortgageDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/mortgages/affordability" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.MORTGAGE_VIEW]}>
                                        <AffordabilityView />
                                    </ProtectedRoute>
                                } />
                                <Route path="/mortgages/reports" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.MORTGAGE_VIEW]}>
                                        <MortgageReports />
                                    </ProtectedRoute>
                                } />

                                {/* Card Operations Routes (Phase 9C.6) */}
                                <Route path="/cards" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CARDS_VIEW]}>
                                        <AdminCardList />
                                    </ProtectedRoute>
                                } />
                                <Route path="/cards/disputes" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CARDS_MANAGE]}>
                                        <AdminCardDisputes />
                                    </ProtectedRoute>
                                } />
                                <Route path="/cards/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CARDS_VIEW]}>
                                        <AdminCardDetail />
                                    </ProtectedRoute>
                                } />

                                {/* Smart Panel Routes */}
                                <Route path="/smart-panel" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS_EDIT]}>
                                        <SmartPanelMessages />
                                    </ProtectedRoute>
                                } />
                                <Route path="/smart-panel/create" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS_EDIT]}>
                                        <MessageEditor />
                                    </ProtectedRoute>
                                } />
                                <Route path="/smart-panel/:messageId/edit" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS_EDIT]}>
                                        <MessageEditor />
                                    </ProtectedRoute>
                                } />

                                {/* Rewards Routes */}
                                <Route path="/rewards/config" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <RewardsConfig />
                                    </ProtectedRoute>
                                } />
                                <Route path="/rewards/badges" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <RewardsBadges />
                                    </ProtectedRoute>
                                } />
                                <Route path="/rewards/tiers" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <RewardsTiers />
                                    </ProtectedRoute>
                                } />
                                <Route path="/rewards/offers" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <RewardsOffers />
                                    </ProtectedRoute>
                                } />
                                <Route path="/rewards/analytics" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <RewardsAnalytics />
                                    </ProtectedRoute>
                                } />

                                {/* Insights Routes */}
                                <Route path="/insights" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_INSIGHTS_VIEW]}>
                                        <InsightsManager />
                                    </ProtectedRoute>
                                } />

                                {/* Journey Templates Routes */}
                                <Route path="/journey-templates" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_JOURNEYS_VIEW]}>
                                        <TemplateList />
                                    </ProtectedRoute>
                                } />
                                <Route path="/journey-templates/new" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE]}>
                                        <TemplateEditor />
                                    </ProtectedRoute>
                                } />
                                <Route path="/journey-templates/:templateId" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE]}>
                                        <TemplateEditor />
                                    </ProtectedRoute>
                                } />

                                {/* Profile Traits Routes */}
                                <Route path="/profile-traits" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW]}>
                                        <TraitsManager />
                                    </ProtectedRoute>
                                } />

                                {/* Branch Banking Routes */}
                                <Route path="/branch/dashboard" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_BANKING_ACCESS]}>
                                        <BranchDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/branch/customers" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_BANKING_ACCESS]}>
                                        <CustomerLookup />
                                    </ProtectedRoute>
                                } />
                                <Route path="/branch/accounts/open" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_ACCOUNTS_OPEN]}>
                                        <AccountOpening />
                                    </ProtectedRoute>
                                } />
                                <Route path="/branch/transactions" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_TRANSACTIONS_CREATE]}>
                                        <BranchTransactions />
                                    </ProtectedRoute>
                                } />
                                <Route path="/branch/cash" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_CASH_MANAGE]}>
                                        <CashManagement />
                                    </ProtectedRoute>
                                } />
                                <Route path="/branch/queue" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_BANKING_ACCESS]}>
                                        <TellerQueue />
                                    </ProtectedRoute>
                                } />

                                {/* Audit Trail Routes */}
                                <Route path="/audit" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.AUDIT_VIEW]}>
                                        <AuditDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/audit/log" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.AUDIT_VIEW]}>
                                        <AuditLog />
                                    </ProtectedRoute>
                                } />
                                <Route path="/audit/users" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.AUDIT_VIEW]}>
                                        <AuditUserActivity />
                                    </ProtectedRoute>
                                } />
                                <Route path="/audit/report-builder" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.AUDIT_VIEW]}>
                                        <AuditReportBuilder />
                                    </ProtectedRoute>
                                } />

                                {/* General Ledger Routes */}
                                <Route path="/general-ledger" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <GLDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/general-ledger/accounts" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <ChartOfAccounts />
                                    </ProtectedRoute>
                                } />
                                <Route path="/general-ledger/journals" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <JournalEntries />
                                    </ProtectedRoute>
                                } />
                                <Route path="/general-ledger/journals/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <JournalDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/general-ledger/trial-balance" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <TrialBalance />
                                    </ProtectedRoute>
                                } />

                                {/* Payment Operations Routes */}
                                <Route path="/payment-ops" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_VIEW]}>
                                        <PaymentsDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payment-ops/search" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_VIEW]}>
                                        <PaymentSearch />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payment-ops/reconciliation" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_RECONCILE]}>
                                        <PaymentOpsReconciliation />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payment-ops/standing-orders" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_VIEW]}>
                                        <PaymentOpsStandingOrders />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payment-ops/failed" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_MANAGE]}>
                                        <PaymentOpsFailedPayments />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payment-ops/scheme-routing" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_SCHEME_CONFIG]}>
                                        <PaymentOpsSchemeRouting />
                                    </ProtectedRoute>
                                } />

                                {/* Payments Hub Routes (9C.7) */}
                                <Route path="/payments-hub" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_VIEW]}>
                                        <AdminPaymentList />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payments-hub/initiate" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_MANAGE]}>
                                        <AdminInitiatePayment />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payments-hub/schemes" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_SCHEME_CONFIG]}>
                                        <AdminPaymentSchemes />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payments-hub/standing-orders" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_VIEW]}>
                                        <AdminStandingOrders />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payments-hub/direct-debits" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_VIEW]}>
                                        <AdminDirectDebits />
                                    </ProtectedRoute>
                                } />
                                <Route path="/payments-hub/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_VIEW]}>
                                        <AdminPaymentDetail />
                                    </ProtectedRoute>
                                } />

                                {/* GL Enrichment Routes (9C.8) */}
                                <Route path="/gl/accounts" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <AdminChartOfAccounts />
                                    </ProtectedRoute>
                                } />
                                <Route path="/gl/journals" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <AdminJournalEntries />
                                    </ProtectedRoute>
                                } />
                                <Route path="/gl/trial-balance" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <AdminTrialBalance />
                                    </ProtectedRoute>
                                } />
                                <Route path="/gl/financial-statements" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <AdminFinancialStatements />
                                    </ProtectedRoute>
                                } />
                                <Route path="/gl/cost-centres" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_VIEW]}>
                                        <AdminCostCentres />
                                    </ProtectedRoute>
                                } />
                                <Route path="/gl/reconciliation" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PAYMENTS_RECONCILE]}>
                                        <AdminReconciliation />
                                    </ProtectedRoute>
                                } />
                                <Route path="/gl/periods" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.GL_MANAGE]}>
                                        <AdminPeriodManagement />
                                    </ProtectedRoute>
                                } />

                                {/* KYC Management Routes */}
                                <Route path="/kyc" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_VIEW]}>
                                        <KycDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/kyc/reviews" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_MANAGE]}>
                                        <KycReviews />
                                    </ProtectedRoute>
                                } />
                                <Route path="/kyc/analytics" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_VIEW]}>
                                        <KycAnalytics />
                                    </ProtectedRoute>
                                } />

                                {/* Compliance Routes */}
                                <Route path="/compliance" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_VIEW]}>
                                        <ComplianceDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/alerts" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_VIEW]}>
                                        <AlertManagement />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/sar" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_SAR_FILE]}>
                                        <SarFiling />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/regulatory-returns" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_VIEW]}>
                                        <RegulatoryReturns />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/sanctions" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_SANCTIONS_SCREEN]}>
                                        <SanctionsScreening />
                                    </ProtectedRoute>
                                } />

                                {/* Compliance Centre – Phase 9C.9 */}
                                <Route path="/compliance/sar-management" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_SAR_FILE]}>
                                        <AdminSARManagement />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/sanctions-screening" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_SANCTIONS_SCREEN]}>
                                        <AdminSanctionsScreening />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/transaction-alerts" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.COMPLIANCE_VIEW]}>
                                        <AdminTransactionAlerts />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/regulatory-reports" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.REGULATORY_REPORTS_VIEW]}>
                                        <AdminRegulatoryReports />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/capital-adequacy" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.REGULATORY_CAPITAL_VIEW]}>
                                        <AdminCapitalAdequacy />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/ifrs9" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.REGULATORY_REPORTS_VIEW]}>
                                        <AdminIFRS9Dashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/compliance/rwa" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.REGULATORY_CAPITAL_VIEW]}>
                                        <AdminRiskWeightedAssets />
                                    </ProtectedRoute>
                                } />

                                {/* KYB Centre – Phase 9C.10 */}
                                <Route path="/kyb/entities" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.KYB_ENTITIES_VIEW]}>
                                        <KYBEntityList />
                                    </ProtectedRoute>
                                } />
                                <Route path="/kyb/entities/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.KYB_ENTITIES_VIEW]}>
                                        <KYBEntityDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/kyb/reviews" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.KYB_REVIEWS_VIEW]}>
                                        <KYBReviewQueue />
                                    </ProtectedRoute>
                                } />
                                <Route path="/kyb/screening" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.KYB_SCREENING_VIEW]}>
                                        <KYBScreeningDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/kyb/ownership/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.KYB_ENTITIES_VIEW]}>
                                        <KYBOwnershipStructure />
                                    </ProtectedRoute>
                                } />

                                {/* Branch Banking Integration – Phase 9C.11 */}
                                <Route path="/branch/teller" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_BANKING_ACCESS]}>
                                        <AdminTellerDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/branch/vault" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_CASH_MANAGE]}>
                                        <AdminVaultManagement />
                                    </ProtectedRoute>
                                } />
                                <Route path="/branch/eod" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_CASH_MANAGE]}>
                                        <AdminEODReconciliation />
                                    </ProtectedRoute>
                                } />
                                <Route path="/branch/overview" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.BRANCH_BANKING_ACCESS]}>
                                        <AdminBranchOverview />
                                    </ProtectedRoute>
                                } />

                                {/* Notifications Centre – Phase 9C.14 */}
                                <Route path="/notifications/templates" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_NOTIFICATIONS_MANAGE]}>
                                        <AdminNotificationTemplates />
                                    </ProtectedRoute>
                                } />
                                <Route path="/notifications/history" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_NOTIFICATIONS_VIEW]}>
                                        <AdminDeliveryHistory />
                                    </ProtectedRoute>
                                } />
                                <Route path="/notifications/channels" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_NOTIFICATIONS_MANAGE]}>
                                        <AdminChannelConfig />
                                    </ProtectedRoute>
                                } />
                                <Route path="/notifications/preferences" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_NOTIFICATIONS_VIEW]}>
                                        <AdminCustomerPreferences />
                                    </ProtectedRoute>
                                } />

                                {/* Product Factory – Phase 9C.12 */}
                                <Route path="/products" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]}>
                                        <AdminProductCatalog />
                                    </ProtectedRoute>
                                } />
                                <Route path="/products/compare" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]}>
                                        <AdminProductComparison />
                                    </ProtectedRoute>
                                } />
                                <Route path="/products/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]}>
                                        <AdminProductDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/products/builder" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]}>
                                        <AdminProductBuilder />
                                    </ProtectedRoute>
                                } />

                                {/* Limits & Controls – Phase 9C.13 */}
                                <Route path="/limits" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LIMITS_VIEW]}>
                                        <AdminLimitDefinitions />
                                    </ProtectedRoute>
                                } />
                                <Route path="/limits/institution" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LIMITS_VIEW]}>
                                        <AdminInstitutionLimits />
                                    </ProtectedRoute>
                                } />
                                <Route path="/limits/customer-overrides" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LIMITS_VIEW]}>
                                        <AdminCustomerLimitOverrides />
                                    </ProtectedRoute>
                                } />
                                <Route path="/limits/history" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.LIMITS_VIEW]}>
                                        <AdminLimitHistory />
                                    </ProtectedRoute>
                                } />

                                {/* Insights Engine Admin – Phase 9CX.3 */}
                                <Route path="/admin/insights/designer" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_INSIGHTS_MANAGE]}>
                                        <Navigate to="/insights" replace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/insights/rules" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_INSIGHTS_VIEW]}>
                                        <Navigate to="/insights" replace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/insights/schedules" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_INSIGHTS_VIEW]}>
                                        <Navigate to="/insights?tab=schedules" replace />
                                    </ProtectedRoute>
                                } />

                                {/* Profile Engine Admin – Phase 9CX.4 */}
                                <Route path="/admin/profiles/designer" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_PROFILE_TRAITS_MANAGE]}>
                                        <Navigate to="/profile-traits" replace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/profiles/traits" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW]}>
                                        <Navigate to="/profile-traits" replace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/profiles/overrides" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW]}>
                                        <Navigate to="/profile-traits?tab=overrides" replace />
                                    </ProtectedRoute>
                                } />

                                {/* Journey Engine Admin – Phase 9CX.5 */}
                                <Route path="/admin/journeys/templates" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_JOURNEYS_VIEW]}>
                                        <Navigate to="/journey-templates" replace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/journeys/builder/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE]}>
                                        <JourneyBuilderRedirect />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/journeys/versions/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_JOURNEYS_VIEW]}>
                                        <Navigate to="/journey-templates" replace />
                                    </ProtectedRoute>
                                } />

                                {/* Provider Registry Admin – Phase 9CX.6 */}
                                <Route path="/admin/providers" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PROVIDERS_CATALOG_VIEW]}>
                                        <AdminProviderCatalog />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/providers/contracts" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PROVIDERS_CONTRACTS_VIEW]}>
                                        <AdminProviderContracts />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/providers/health" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PROVIDERS_HEALTH_VIEW]}>
                                        <AdminProviderHealth />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/providers/features" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PROVIDERS_CATALOG_VIEW]}>
                                        <AdminFeatureMatrix />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/providers/swap" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PROVIDERS_SWAP]}>
                                        <AdminProviderSwap />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/providers/metering" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PROVIDERS_METERING_VIEW]}>
                                        <AdminApiMetering />
                                    </ProtectedRoute>
                                } />

                                {/* Fee Engine Admin – Phase 9CX.7 */}
                                <Route path="/admin/fees" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.FEES_DEFINITIONS_VIEW]}>
                                        <AdminFeeDefinitions />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/fees/schedules" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.FEES_SCHEDULES_VIEW]}>
                                        <AdminFeeSchedules />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/fees/simulator" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.FEES_SIMULATE]}>
                                        <AdminFeeSimulator />
                                    </ProtectedRoute>
                                } />

                                {/* Rewards Engine Full Admin – Phase 9CX.8 */}
                                <Route path="/admin/rewards" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <Navigate to="/rewards/config" replace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/rewards/badges" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <Navigate to="/rewards/badges" replace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/rewards/milestones" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <Navigate to="/rewards/badges" replace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/rewards/schemes" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <Navigate to="/rewards/config" replace />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/rewards/analytics" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_REWARDS_VIEW]}>
                                        <Navigate to="/rewards/analytics" replace />
                                    </ProtectedRoute>
                                } />

                                {/* Open Banking Admin (Phase 9CX.9) */}
                                <Route path="/admin/openbanking/config" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.OPENBANKING_CONFIG_VIEW]}>
                                        <AdminOpenBankingConfig />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/openbanking/connections" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.OPENBANKING_CONNECTIONS_VIEW]}>
                                        <AdminConnectionMonitor />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/openbanking/alerts" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.OPENBANKING_ALERTS_VIEW]}>
                                        <AdminOpenBankingAlerts />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/openbanking/stats" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.OPENBANKING_STATS_VIEW]}>
                                        <AdminOpenBankingStats />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/openbanking/consents" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.OPENBANKING_CONSENTS_VIEW]}>
                                        <AdminConsentOversight />
                                    </ProtectedRoute>
                                } />

                                {/* ConfigStore Admin (Phase 9CX.12) */}
                                <Route path="/admin/config" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CONFIG_VIEW]}>
                                        <AdminConfigBrowser />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/config/history" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CONFIG_VIEW]}>
                                        <AdminConfigHistory />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/config/export-import" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CONFIG_EXPORT]}>
                                        <AdminConfigExportImport />
                                    </ProtectedRoute>
                                } />

                                {/* Rules Config – Phase 9CX.1 */}
                                <Route path="/admin/rules" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS_EDIT]}>
                                        <AdminRulesConfig />
                                    </ProtectedRoute>
                                } />

                                {/* Neural Engine Admin – Phase 9CX.2 */}
                                <Route path="/admin/neural/personality" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.NEURAL_PERSONALITY_VIEW]}>
                                        <AdminPersonalityManager />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/neural/rules" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.NEURAL_RULES_VIEW]}>
                                        <AdminNeuralRules />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/neural/feedback" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.NEURAL_FEEDBACK_VIEW]}>
                                        <AdminFeedbackDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/neural/conversations" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.NEURAL_SESSIONS_VIEW]}>
                                        <AdminConversationBrowser />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/moni-ai/feedback" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSTITUTION_MONI_FEEDBACK_VIEW]}>
                                        <AdminMoniAIFeedbackIssues />
                                    </ProtectedRoute>
                                } />


                                {/* Platform Management – Phase 9E.1 System Health */}
                                <Route path="/platform/health" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_HEALTH_VIEW]}>
                                        <PlatformSystemHealth />
                                    </ProtectedRoute>
                                } />
                                <Route path="/platform/health/:name" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_HEALTH_VIEW]}>
                                        <PlatformServiceDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/platform/infrastructure" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_HEALTH_VIEW]}>
                                        <PlatformInfrastructureHealth />
                                    </ProtectedRoute>
                                } />

                                {/* Platform Management – Phase 9E.2 Metering & Billing */}
                                <Route path="/platform/metering" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_METERING_VIEW]}>
                                        <PlatformMeteringDashboard />
                                    </ProtectedRoute>
                                } />
                                <Route path="/platform/metering/usage/:institutionId" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_METERING_VIEW]}>
                                        <PlatformUsageDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/platform/billing/plans" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_BILLING_MANAGE]}>
                                        <PlatformBillingPlans />
                                    </ProtectedRoute>
                                } />
                                <Route path="/platform/billing/invoices" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.PLATFORM_METERING_VIEW]}>
                                        <PlatformInvoiceManagement />
                                    </ProtectedRoute>
                                } />

                                {/* AI Reports & Data Explorer – Phase 9D.3 / 9D.4 */}
                                <Route path="/ai/reports" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS_VIEW]}>
                                        <AIReports />
                                    </ProtectedRoute>
                                } />
                                <Route path="/ai/reports/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS_VIEW]}>
                                        <AIReportDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/ai/explorer" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS_VIEW]}>
                                        <AIDataExplorer />
                                    </ProtectedRoute>
                                } />

                                {/* Global Search - requires customers:view */}
                                <Route path="/search" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CUSTOMERS_VIEW]}>
                                        <SearchResults />
                                    </ProtectedRoute>
                                } />

                                {/* Admin Dashboard - placeholder */}
                                <Route path="/admin" element={<AdminDashboard />} />

                                {/* Transactions (Phase 10) */}
                                <Route path="/transactions" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.TRANSACTIONS_VIEW]}>
                                        <TransactionList />
                                    </ProtectedRoute>
                                } />
                                <Route path="/transactions/:id" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.TRANSACTIONS_VIEW]}>
                                        <TransactionDetail />
                                    </ProtectedRoute>
                                } />

                                {/* Insurance Admin (Phase 9C.18) */}
                                <Route path="/admin/insurance/products" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSURANCE_PRODUCTS_MANAGE]}>
                                        <AdminInsuranceProducts />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/insurance/quotes" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSURANCE_POLICIES_MANAGE]}>
                                        <AdminInsuranceQuotes />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/insurance/underwriting" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSURANCE_POLICIES_MANAGE]}>
                                        <AdminInsuranceUnderwriting />
                                    </ProtectedRoute>
                                } />
                                <Route path="/admin/insurance/claims" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.INSURANCE_CLAIMS_MANAGE]}>
                                        <AdminInsuranceClaims />
                                    </ProtectedRoute>
                                } />

                                {/* Migration Console (Phase 9E.8) */}
                                <Route path="/platform/migration" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.CONFIG_MANAGE]}>
                                        <PlatformMigrationConsole />
                                    </ProtectedRoute>
                                } />

                                {/* Settings Route - requires settings:view */}
                                <Route path="/settings" element={
                                    <ProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS_VIEW]}>
                                        <Settings />
                                    </ProtectedRoute>
                                } />
                                <Route path="/unauthorized" element={<Unauthorized />} />
                                <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

export { PageLoader };
export default AppRoutes;
